/**
 * B31g (TRA-79) — buyer-language refusal translation. The §11 wire speaks
 * operator ("Cart cannot be sold as constituted"); buyers get buyer words.
 * The map keys on the EXACT wire message (the handler refusal maps pin
 * those strings in their own tests); anything unrecognized falls back to
 * generic copy. THE WALL: the raw wire message never renders — this module
 * returns only sentences written here.
 */

export interface WireRefusal {
  readonly status: number;
  readonly code: string;
  readonly message: string;
  readonly retryable: boolean;
}

/** Exact wire message → buyer copy. Keep alphabetical-ish; values only ever buyer words. */
/** B442: exported for the mobile fixture generator — the table IS the contract the twins replay. */
export const BUYER_COPY: Readonly<Record<string, string>> = {
  // commerce (cart.create/add/remove/apply_promo/checkout, cart.get)
  "No such cart": "We couldn't find your cart — it may have expired. Please start again.",
  "No such cart line": "That ticket is no longer in your cart.",
  "Cart id already exists": "Something went wrong starting your cart — please try again.",
  "Cart is checked out and no longer accepts changes":
    "This order is already on its way to payment. Start a new cart to buy more tickets.",
  "Cart cannot be sold as constituted":
    "Some tickets in your cart need adjusting before checkout — see the notes on your cart.",
  "Not enough capacity to hold this cart":
    "There isn't enough space left for your whole party — pick another time or reduce quantities.",
  // B549 — the groups interrupt: the buyer surface offers the inquiry, not a dead end.
  "A booking this size goes through the groups team":
    "Booking for a big group? Our groups team will arrange it — send a group inquiry and they'll take care of you.",
  "Promotion usage cap exhausted": "That promo code has been fully redeemed.",
  "Order already exists for this cart": "This cart is already checked out — your order is in progress.",
  "Concurrent cart change; reload and retry": "Your cart just changed (another tab?) — please try again.",
  "Capacity is contended right now; please retry":
    "Lots of people are buying right now — give it a moment and try again.",
  // catalog
  "No catalog": "Nothing is on sale right now.",
  // payment.intent.create / order reads
  "No such order": "We couldn't find that order.",
  "No such payment intent": "We couldn't find that payment.",
  "Order is not awaiting payment": "This order isn't waiting on a payment.",
  "Tender amount cannot be collected on this order": "That payment amount doesn't match this order.",
  "Unknown payment provider": "Payments aren't available right now — please try again shortly.",
  "Provider vended no client secret; the payment surface cannot mount":
    "The payment form couldn't load — give it a moment and try again.",
  "Order cannot be confirmed as recorded": "This payment couldn't be matched to your order.",
  "Capacity holds lost or expired; the order can no longer confirm":
    "Your reservation expired before payment completed — please start again.",
  "Tenders do not sum to the order total": "That payment doesn't cover the order total.",
  "Concurrent payment activity; reload and retry": "The payment is already being processed — hold on and try again.",
  // entitlements
  "Not found": "We couldn't find that ticket.",
  "Entitlement is voided": "This ticket has been cancelled.", // vocab-ok: kernel wire-message lookup key, byte-exact — the rendered face is the value
};

export const GUEST_DOOR = "Guest access isn't enabled on this stage yet.";
export const GENERIC = "That didn't work — adjust your order and try again.";
export const GENERIC_RETRY = "Something went wrong — please try again.";

/**
 * B169(b) — the two faces for failures that are NOT wire refusals. They
 * live here, not in App.tsx, for the same reason every sentence above
 * does: the container renders copy, it does not author it.
 */

/** Something unexpected, but plausibly transient — retrying is honest advice. */
export const UNKNOWN_FAILURE = "Something went wrong. Please try again.";

/**
 * A refusal raised BEFORE anything reached the wire: this page asked in a
 * shape the service cannot accept. That is a fault in the page, so the
 * copy must not promise a retry that cannot work (FORMS.md R12 — say what
 * failed and what to do).
 */
export const PAGE_MISCONFIGURED =
  "This page can't complete that step — it's out of date or wasn't set up correctly. Reloading won't fix it; whoever runs this site needs to update it.";

/** Wire refusal → the ONLY copy the buyer UI may render for it. */
export function translateRefusal(refusal: WireRefusal): string {
  if (refusal.status === 401 || refusal.status === 403) return GUEST_DOOR;
  const mapped = BUYER_COPY[refusal.message];
  if (mapped !== undefined) return mapped;
  return refusal.retryable || refusal.status >= 500 ? GENERIC_RETRY : GENERIC;
}
